import axios from "axios";
import { X_API_KEY } from "../constants";
import IdentityClient from "./IdentityClient";
import { Cache } from "../util/Cache.util";
import OfflineInterceptor from "../util/OfflineInterceptor";
import OfflineSyncQueue from "../util/OfflineSyncQueue";
import Dialog from "../util/Dialog";

export class LiveApiCache {
	static #AUTH_EXPIRATION_SECS = 60 * 60 * 24 * 60; // match Live's "auth" expiration
	/** @type Promise */
	static SharedTokenFetch;
	
	static Set(token) {
		if (!token) {
			return;
		}
		Cache.Local().set("api-token", { token, expires_on: this.#Get_Expiration() });
	}

	static Get() {
		const stored = Cache.Local().get("api-token");
		if (stored?.expires_on > Date.now()) {
			return stored.token;
		}
		LiveApiCache.Reset();
	}

	static Reset() {
		Cache.Local().set("api-token", undefined);
	}

	static #Get_Expiration() {
		return Date.now() + (this.#AUTH_EXPIRATION_SECS * 1000);
	}
}

// Dev API override: ?api=URL sets it, persists in sessionStorage.
// ?api=reset clears it. Shown as a banner when active.
const _isDev = process.env.NODE_ENV !== 'production';
const _apiParam = _isDev && new URLSearchParams(window.location.search).get('api');
if (_apiParam === 'reset') {
	sessionStorage.removeItem('__api_override');
} else if (_apiParam) {
	sessionStorage.setItem('__api_override', _apiParam);
}
const _apiOverride = _isDev && sessionStorage.getItem('__api_override');
const _apiBaseURL = _apiOverride || process.env.REACT_APP_API_CLIENT_BASE_URL || "https://staging.bosslogics.live/api";

if (_isDev) {
	console.warn(`%c[API] ${_apiBaseURL}` + (_apiOverride ? '  (override — ?api=reset to clear)' : ''), 'color:#0099ff;font-weight:bold');
	if (_apiOverride) {
		const banner = document.createElement('div');
		banner.innerHTML = `API: <b>${_apiOverride}</b> <span style="opacity:0.6;margin-left:6px">· ?api=reset</span>`;
		Object.assign(banner.style, {
			position: 'fixed', bottom: '4px', left: '4px', zIndex: '99999',
			background: '#1e40af', color: '#fff', fontSize: '10px', padding: '2px 8px',
			borderRadius: '4px', opacity: '0.85', pointerEvents: 'none',
		});
		document.body?.appendChild(banner) || window.addEventListener('DOMContentLoaded', () => document.body.appendChild(banner));
	}
}

const LiveApiClient = axios.create({
	baseURL: _apiBaseURL,
	headers: {
		Authorization: "",
		Project: "together.boss",
		Accept: "application/json",
		'Cache-Control': 'no-cache',
		// 'Pragma': 'no-cache',
		'Expires': '0',
	},
});

LiveApiClient.interceptors.request.use(
	async config => {
		// After "Update Software", send __recache-page header on first page load
		if (sessionStorage.getItem('__recache_pending')) {
			config.headers['__recache-page'] = '1';
		}

		// Default count=false for standard GET requests (skip expensive count queries)
		// Excludes fn/ endpoints (custom functions) and /count endpoints (explicit count requests)
		if (config.method === 'get' && !config.url?.includes('/fn/') && !config.url?.endsWith('/count')) {
			config.params = config.params || {};
			if (config.params.count === undefined) {
				config.params.count = false;
			}
		}

		const savedToken = LiveApiCache.Get();
		
		const checkToken = token => {
			const base64Url = token.split('.').at(1);
			if (!base64Url) return false;
			const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

			const jsonPayload = decodeURIComponent(window.atob(base64).split('')
				.map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
			if (!jsonPayload) {
				return false;
			}
			
			try {
				const payload = JSON.parse(jsonPayload);
				// 60s buffer for clock skew between client and server
				return !(payload.exp && (payload.exp * 1000 - 60000) < Date.now());
			} catch (e) {
				return false;
			}
		}
		
		if (savedToken && checkToken(savedToken)) {
			config.headers.Authorization = savedToken || '';
			return config;
		}

		if (X_API_KEY || config.headers['x-api-key']) {
			if (X_API_KEY) config.headers['x-api-key'] = X_API_KEY;
			return config;
		}

		LiveApiCache.SharedTokenFetch ??= IdentityClient.post("getToken")
			.finally(() => { LiveApiCache.SharedTokenFetch = undefined; });

		const { data: { token } } = await LiveApiCache.SharedTokenFetch;
		config.headers.Authorization = token;
		LiveApiCache.Set(token);

		Object.keys(config.headers).forEach(key => {
			if (config.headers[key] === undefined) {
				delete config.headers[key];
			}
		});

		return config;
	},
	error => Promise.reject(error)
);

/** Shared refresh promise so concurrent 401s only trigger one getToken call */
let _refresh_promise = null;

LiveApiClient.interceptors.response.use(
	response => {
		// Clear recache flag after first successful response post-update
		if (sessionStorage.getItem('__recache_pending')) {
			sessionStorage.removeItem('__recache_pending');
		}
		return response;
	},
	async error => {
		// Ignore cancelled/aborted requests (e.g., from AbortController)
		if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
			return Promise.reject(error);
		}

		// Handle network errors (no response = no internet or server unreachable)
		if (!error.response) {
			Dialog.fire({
				toast: true,
				position: 'top-end',
				icon: 'error',
				title: 'Connection lost. Please check your internet.',
				showConfirmButton: false,
				timer: 5000,
			});
			return Promise.reject(error);
		}

		if (error.response?.status !== 401) {
			return Promise.reject(error);
		}

		const original_config = error.config;

		const clearSessionAndRedirect = () => {
			LiveApiCache.Reset();
			localStorage.removeItem('catalog_invitation:data');
			localStorage.removeItem("organization_member:data");
			localStorage.removeItem("currentUser");
			window.location = '/login?redirect=' + window.location.pathname;
		};

		// Preflight requests can't be refreshed/retried meaningfully
		if (original_config.method === "options") {
			return Promise.reject(error);
		}

		// Already refreshed once and the retry came back 401 — session is dead.
		// Redirect silently like the no-token branch does, otherwise the rejection
		// surfaces as an unhandled promise (JS-4C2: Profile.GetCurrent in
		// SelectCatalog) even though the UX outcome is correct.
		if (original_config._retry) {
			clearSessionAndRedirect();
			return new Promise(() => {}); // page is navigating away
		}

		// If using a static X_API_KEY (microsites), no token refresh is possible
		if (X_API_KEY) {
			return Promise.reject(error);
		}

		// Attempt token refresh and retry the original request
		original_config._retry = true;

		try {
			_refresh_promise ??= IdentityClient.post("getToken")
				.finally(() => { _refresh_promise = null; });
			const { data: { token } } = await _refresh_promise;

			if (!token) {
				// Session truly expired — redirect silently. Don't throw: the
				// old "No token returned" throw was caught here and re-rejected,
				// which bubbled up as Sentry noise (JS-5F9 cluster) even though
				// the UX is correct (user just gets logged out).
				clearSessionAndRedirect();
				return new Promise(() => {}); // page is navigating away
			}

			LiveApiCache.Set(token);
			original_config.headers.Authorization = token;
			return LiveApiClient(original_config);
		} catch (refresh_error) {
			// Genuine refresh failure (network, server error) — surface it
			// for debugging in addition to logging the user out.
			clearSessionAndRedirect();
			return Promise.reject(refresh_error);
		}
	}
);

// Install offline interceptor (caching + offline fallback + write queuing)
OfflineInterceptor.Install(LiveApiClient);
OfflineSyncQueue.Init(LiveApiClient);

export default LiveApiClient;
